import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import { ReactComponent as DialpadIcon } from '@shared/assets/icons/ic-dialer.svg'
import {
  ArrowUpDown,
  Merge,
  MessageSquareShare,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneIncoming,
  UserRoundPlus,
} from 'lucide-react'
import { IVRKeypad } from 'v2/pages/flow/components/ivr-dialer'
import { ElapsedTimer } from 'v2/shared/components/elapsed-timer'
import { usePhoneStore } from 'v2/state/phone'
import { CallDirection, ParticipantState } from 'v2/types/call'
import { getLocalAuth } from 'v2/utils'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { useTransferMutations } from '../../mutations/conversations'
import { useConversationTasksQuery } from '../../tasks/queries'
import {
  checkAgentHasOpenTasks,
  checkSecondaryParticipant,
  isOneOfMonitoringStates,
} from '../../utils'
import { useCallContext } from './call-context'
import { MainControls } from './monitoring/main-controls'
import { getParticipantValidation } from './utils'

interface CallControlsProps {
  isMinimized: boolean
}

export const CallControls = ({ isMinimized }: CallControlsProps) => {
  const {
    isIncomingCallRinging,
    isHoldCallPending,
    showConversationButton,
    showTransferModal,
    handleToggleHold,
    handleNavigateToConversation,
    handleShowTransferModal,
  } = useCallContext()
  const {
    participants,
    isMuted,
    isOnHold,
    callPickedup,
    isCallActive,
    isSecondaryParticipant,
    activeCall,
  } = usePhoneStore()
  const callConversation = usePhoneStore((state) => state.callConversation)
  const { mergeParticipant, toggleCall } = useTransferMutations()
  const { data: conversationTasks } = useConversationTasksQuery(
    callConversation?.parentConversation?.conversation_number as string,
  )
  const { aom_uuid: agentId } = getLocalAuth()

  const { canAdd, disabledReason } = getParticipantValidation({
    // Filter out monitor participant
    participants: participants.filter((p) => p.participantType !== 'monitor'),
    hasOpenTasks: checkAgentHasOpenTasks(conversationTasks?.tasks || [], agentId),
  })

  const handleMerge = () => {
    mergeParticipant.mutate(
      { contact_uuid: callConversation?.callChildConversation?.contact_uuid },
      {
        onSuccess: () => {
          usePhoneStore.getState().setOnHold(false)
          usePhoneStore.getState().updateParticipant(participants[0].member, {
            isOnHold: false,
          })
        },
      },
    )
  }

  const connectedParticipants = participants.find((p) => p.state === ParticipantState.CONNECTED)
  const isCallConnecting = activeCall?.direction === CallDirection.INBOUND && !callPickedup
  const monitorState = usePhoneStore((state) => state.monitoringState)
  const isMonitorInitiator = usePhoneStore((state) => state.isMonitorInitiator)

  // Feature: CALL_MONITORING
  // TODO: Check if we should check with monitoringState from phoneStore instead of callConversation
  const isMonitoringState = isOneOfMonitoringStates(monitorState)

  // Feature: CALL_MONITORING
  if (
    isMonitoringState &&
    checkSecondaryParticipant(callConversation.parentConversation) &&
    isMonitorInitiator
  )
    return <MainControls isMinimized={isMinimized} />

  if (isIncomingCallRinging && activeCall?.autoAccept) {
    return (
      <div className="flex items-center gap-2 -ml-5">
        <Button variant="default" size="sm" className="w-full gap-1" onClick={phoneSDK.answerCall}>
          <PhoneIncoming className="h-4 w-4 mr-2" />
          Auto accepting in{' '}
          <ElapsedTimer
            countdownDuration={activeCall?.autoAcceptDelay}
            startTime={activeCall.startTime.toISOString()}
            format="s"
          />
          <span className="-ml-1">s</span>
        </Button>
      </div>
    )
  }

  // Controls shown when incoming call is ringing
  if (isIncomingCallRinging) {
    // if (false) {
    return (
      <div className={cn('flex items-center justify-around', !isMinimized && '-ml-5')}>
        <Button
          onClick={phoneSDK.rejectCall}
          className="w-32"
          variant="destructive-outline"
          size="sm"
          data-testid="button-reject-call"
        >
          <Phone className="h-4 w-4 fill-destructive rotate-[135deg] mr-2 stroke-none" />
          Reject
        </Button>
        <Button
          onClick={phoneSDK.answerCall}
          className="w-32"
          variant="success"
          size="sm"
          data-testid="button-accept-call"
        >
          <PhoneIncoming className="h-4 w-4 mr-2" />
          Accept
        </Button>
      </div>
    )
  }

  // Controls shown when call is active - incoming or outbound
  return (
    <div className="flex items-center gap-2">
      {/* Mute button - always visible */}
      <Tooltip content={isMuted ? 'Unmute' : 'Mute'}>
        <Button
          variant="tertiary"
          size="iconSm"
          onClick={phoneSDK.toggleMute}
          data-testid="button-mute-call"
          className={cn(
            'rounded-full',
            isMuted && 'bg-primary text-primary-foreground hover:bg-primary/80',
          )}
        >
          {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
      </Tooltip>

      {/* Hold button - hide for secondary participant */}
      {!isMinimized &&
        !isSecondaryParticipant &&
        callPickedup &&
        (!participants.length || participants[0].state === ParticipantState.MERGED) && (
          <>
            <Tooltip content={isOnHold ? 'Unhold' : 'Hold'}>
              <Button
                variant="tertiary"
                size="iconSm"
                data-testid="button-hold-call"
                disabled={isHoldCallPending}
                onClick={handleToggleHold}
                className={cn(
                  'rounded-full',
                  isOnHold && 'bg-primary text-primary-foreground hover:bg-primary/80',
                )}
              >
                <Pause className="h-4 w-4" />
              </Button>
            </Tooltip>
            {/* Add IVR keypad here. */}
            <Tooltip content="IVR Keypad">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="tertiary" size="iconSm" className="rounded-full">
                    <DialpadIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <IVRKeypad
                    value={[]}
                    onChange={() => {}}
                    label="Send DTMF"
                    onKeySelect={(key) => phoneSDK.sendDTMF(key)}
                  />
                </PopoverContent>
              </Popover>
            </Tooltip>
          </>
        )}

      {/* Toggle Call button - hide for secondary participant */}
      {!isMinimized && !isSecondaryParticipant && connectedParticipants && callPickedup && (
        <Button
          variant="tertiary"
          tooltipContent="Switch to participant"
          size="iconSm"
          className="rounded-full"
          data-testid="button-switch-to-participant"
          disabled={toggleCall.isPending || !connectedParticipants.isOnHold}
          disabledReason={
            !connectedParticipants.isOnHold
              ? 'Cannot switch - already talking to participant'
              : toggleCall.isPending
                ? 'Switching call...'
                : ''
          }
          onClick={() =>
            toggleCall.mutate(
              { contact_uuid: callConversation?.callChildConversation?.contact_uuid },
              {
                onSuccess: () => {
                  usePhoneStore.getState().setOnHold(!!connectedParticipants.isOnHold)
                  usePhoneStore.getState().updateParticipant(connectedParticipants.member, {
                    isOnHold: !connectedParticipants.isOnHold,
                  })
                },
              },
            )
          }
        >
          <ArrowUpDown className="size-4" />
        </Button>
      )}

      {/* Invite participant or Transfer button - hide for secondary participant */}
      {!isMinimized && !isSecondaryParticipant && callPickedup && (
        <Tooltip content={canAdd ? 'Invite participant' : disabledReason}>
          <Button
            variant={showTransferModal ? 'default' : 'tertiary'}
            size="iconSm"
            className="rounded-full"
            data-testid="button-invite-participant"
            disabledReason={disabledReason}
            onClick={() => handleShowTransferModal(!showTransferModal)}
            disabled={!canAdd}
          >
            <UserRoundPlus className="h-4 w-4" />
          </Button>
        </Tooltip>
      )}

      {/* Go to conversation button - always visible when conversation exists */}
      {showConversationButton && isCallActive && (
        <Tooltip content="Go to conversation">
          <Button
            variant="tertiary"
            size="iconSm"
            className="rounded-full"
            data-testid="button-go-to-conversation"
            onClick={handleNavigateToConversation}
          >
            <MessageSquareShare className="h-4 w-4" />
          </Button>
        </Tooltip>
      )}

      {/* Merge calls button - hide for secondary participant */}
      {!isMinimized && !isSecondaryParticipant && connectedParticipants && (
        <Tooltip content="Merge calls">
          <Button
            variant="tertiary"
            size="iconSm"
            onClick={handleMerge}
            data-testid="button-merge-call"
            disabled={mergeParticipant.isPending}
            className="rounded-full"
          >
            <Merge className="size-4" />
          </Button>
        </Tooltip>
      )}

      {/* End call button - always visible */}
      <Tooltip content="End Call">
        <Button
          variant="destructive-outline"
          size="iconSm"
          data-testid="button-end-call"
          onClick={phoneSDK.hangup}
          disabled={isCallConnecting}
          className="rounded-full"
        >
          <Phone className="h-4 w-4 fill-destructive rotate-[135deg]" />
        </Button>
      </Tooltip>
    </div>
  )
}
